import type { ISetArticleAvailabilityCmd } from "@/data/commands/operator/ISetArticleAvailabilityCmd";
import type { ISetArticleTempUnavailableCmd } from "@/data/commands/operator/ISetArticleTempUnavailableCmd";
import type { IUpdateBillManualEndUsersCountCmd } from "@/data/commands/operator/IUpdateBillManualEndUsersCountCmd";
import type { IUpdateBillNameCmd } from "@/data/commands/operator/IUpdateBillNameCmd";
import type { IRemoveGuestsFromTableCmd } from "@/data/commands/operator/RemoveGuestsFromTableCmd";
import type { ISetFulfillmentFulfilledCmd } from "@/data/commands/operator/SetFulfillmentFulfilledCmd";
import type { ISetFulfillmentReadyCmd } from "@/data/commands/operator/SetFulfillmentReadyCmd";
import type { IUpdateFulfillmentPositionCmd } from "@/data/commands/operator/UpdateFulfillmentPositionCmd";
import type { IUpdateFulfillmentPreparationTimeCmd } from "@/data/commands/operator/UpdateFulfillmentPreparationTimeCmd";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IStockTableViewArticle } from "@/data/model/Article";
import type {
    IBillTable,
    IOpenBill,
    IOperatorFulfillment,
    IRoomPlanTable,
    ITableCovidUserCount,
} from "@/data/model/Operator";
import type { IOpenBillsQuery } from "@/data/queries/operator/IOpenBillsQuery";
import type { IOperatorFulfillmentsQry } from "@/data/queries/operator/OperatorFulfillmentsQry";
import type { IOperatorStockArticlesQry } from "@/data/queries/operator/OperatorStockArticlesQry";
import type { ITableCovidUserCountQry } from "@/data/queries/operator/TableUserCountQry";
import type { IRoomPlanQuery, ITableQuery as ITableDetailsQuery } from "@/data/queries/operator/TablesStateQuery";
import { DateTime } from "@/data/util/DateTime";

import { type IPaginatedResult } from "./BaseServiceDao";
import { BaseServiceDao, parseCollection, parseMultiLanguageText, parsePaginatedResult } from "./BaseServiceDao";
import { OrderDao } from "./OrderDao";
import { TableDao } from "./TableDao";

export class OperatorDao extends BaseServiceDao {
    public static className = "OperatorDao";

    constructor() {
        super("operator");
        this.isLocationDependent = true;
    }

    public async getTableAndUserCount(qry: ITableCovidUserCountQry): Promise<Array<ITableCovidUserCount>> {
        return this.get("tablecovidusercount", qry, (f) => parseCollection(f, OperatorDao.parseTableCovidUserCount));
    }

    public async getRoomPlan(qry: IRoomPlanQuery): Promise<Array<IRoomPlanTable>> {
        return this.get("roomplan", qry, (f) => parseCollection(f, OperatorDao.parseRoomPlan));
    }

    public async getTableDetails(qry: ITableDetailsQuery): Promise<IRoomPlanTable> {
        return this.get("table-details", qry, OperatorDao.parseRoomPlan);
    }

    public async getOpenBills(qry: IOpenBillsQuery): Promise<Array<IOpenBill>> {
        return this.get("open-bills", qry, (f) => parseCollection(f, OperatorDao.parseOpenBill));
    }

    public async getStockArticles(qry: IOperatorStockArticlesQry): Promise<IPaginatedResult<IStockTableViewArticle>> {
        return this.get("article/stock", qry, (f) => parsePaginatedResult(f, CatalogDao.parseStockTableViewArticle));
    }

    public async getFulfillments(qry: IOperatorFulfillmentsQry = {}): Promise<Array<IOperatorFulfillment>> {
        return this.get("fulfillment", qry, (f) => parseCollection(f, OperatorDao.parseOperatorFulfillment));
    }

    public async setFulfillmentReady(id: string, cmd: ISetFulfillmentReadyCmd): Promise<void> {
        return this.post(`fulfillment/${id}/ready`, cmd);
    }

    public async setFulfillmentFulfilled(id: string, cmd: ISetFulfillmentFulfilledCmd): Promise<void> {
        return this.post(`fulfillment/${id}/fulfilled`, cmd);
    }

    public async updateFulfillmentTime(id: string, cmd: IUpdateFulfillmentPreparationTimeCmd): Promise<void> {
        return this.put(`fulfillment/${id}/time`, cmd);
    }

    public async updateFulfillmentPosition(id: string, cmd: IUpdateFulfillmentPositionCmd): Promise<void> {
        return this.put(`fulfillment/${id}/position`, cmd);
    }

    public async removeGuests(cmd: IRemoveGuestsFromTableCmd): Promise<ITableCovidUserCount> {
        return this.post("removeguests", cmd, OperatorDao.parseTableCovidUserCount);
    }

    public async setArticleAvailability(id: string, cmd: ISetArticleAvailabilityCmd): Promise<IStockTableViewArticle> {
        return this.put(`article/${id}/availability`, cmd);
    }

    public async setArticleTempUnavailable(
        id: string,
        cmd: ISetArticleTempUnavailableCmd
    ): Promise<IStockTableViewArticle> {
        return this.put(`article/${id}/set-temporary-unavailable`, cmd);
    }

    public async updateBillName(id: string, cmd: IUpdateBillNameCmd) {
        return this.put(`bill/${id}/name`, cmd);
    }

    public async updateBillManualEndUsersCount(id: string, cmd: IUpdateBillManualEndUsersCountCmd) {
        return this.put(`bill/${id}/end-users-count`, cmd);
    }

    public static parseTableCovidUserCount(literal: any): ITableCovidUserCount {
        const base: ITableCovidUserCount = TableDao.parseTable(literal) as ITableCovidUserCount;
        base.allocations = parseInt(literal.allocations);
        return base;
    }

    public static parseRoomPlan(literal: any): IRoomPlanTable {
        const base: IRoomPlanTable = TableDao.parseTable(literal) as IRoomPlanTable;
        base.allocations = parseInt(literal.allocations);
        base.timestamp = literal.timestamp == null ? undefined : DateTime.parseIsoDate(literal.timestamp);
        base.status = parseInt(literal.status);
        base.openBillAmount = literal.openBillAmount == null ? undefined : parseFloat(literal.openBillAmount);
        base.manualEndUsersCount =
            literal.manualEndUsersCount == null ? undefined : parseInt(literal.manualEndUsersCount);
        return base;
    }

    public static parseOperatorFulfillment(literal: any): IOperatorFulfillment {
        return {
            id: literal.id,
            locationId: literal.locationId,
            fulfillmentGroupId: literal.fulfillmentGroupId == null ? undefined : literal.fulfillmentGroupId,
            orderId: literal.orderId,
            orderNumber: literal.orderNumber,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            tableName: literal.tableName == null ? undefined : literal.tableName,
            useCaseName: literal.useCaseName == null ? undefined : parseMultiLanguageText(literal.useCaseName),
            roomId: literal.roomId,
            orderDate: DateTime.parseIsoDate(literal.orderDate),
            amendedReadyDateTime: DateTime.parseIsoDate(literal.amendedReadyDateTime),
            scheduledFulfillmentDateTime: DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
            effectiveFulfillmentDateTime:
                literal.effectiveFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveFulfillmentDateTime),
            effectiveReadyDateTime:
                literal.effectiveReadyDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveReadyDateTime),
            isFulfilled: literal.isFulfilled,
            items: parseCollection(literal.items, OrderDao.parseOrderItem),
            isPaid: literal.isPaid,
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            state: parseInt(literal.state),
            currentQueuePosition:
                literal.currentQueuePosition == null ? undefined : parseInt(literal.currentQueuePosition),
            orderedByWaiterName: literal.orderedByWaiterName != null ? literal.orderedByWaiterName : undefined,
            orderHasOtherFulfillments: literal.orderHasOtherFulfillments,
            isLinked: literal.isLinked,
            customTableName: literal.customTableName == null ? undefined : literal.customTableName,
        };
    }

    public static parseOpenBill(literal: any): IOpenBill {
        return {
            billId: literal.billId == null ? undefined : literal.billId,
            name: literal.name,
            billCustomName: literal.billCustomName == null ? undefined : literal.billCustomName,
            billType: parseInt(literal.billType),
            openSince: DateTime.parseIsoDate(literal.openSince),
            openBillAmount: parseFloat(literal.openBillAmount),
            operatorName: literal.operatorName != null ? literal.operatorName : undefined,
            allocatedGuests: parseInt(literal.allocatedGuests),
            hasPreorder: literal.hasPreorder,
            latestPreorderDateTime:
                literal.latestPreorderDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.latestPreorderDateTime),
            table: OperatorDao.parseBillTable(literal.table),
        };
    }

    public static parseBillTable(literal: any): IBillTable {
        return {
            locationId: literal.locationId,
            roomId: literal.roomId,
            roomName: literal.roomName,
            tableId: literal.tableId,
            tableName: literal.tableName,
            tableNumber: parseInt(literal.tableNumber),
            seats: parseInt(literal.seats),
        };
    }
}
